// @ts-nocheck — plain DOM script, not built on @theme/component's typed
// refs like the rest of this theme's assets; see popover-polyfill.js for
// the theme's other precedent of opting a non-Component file out of checkJs.
//
// SKU Configurator
// Mirrors SKU_Matrix_TL logic: Generated SKU = code concatenation,
// SKU Display Name = looked-up labels joined with " | ".
// SKU segment order (both SKU and display) is fixed: shape, finish, stand,
// socket, cord. Shade is deliberately excluded from both — per the sheet,
// shades are upsell SKUs, not SKU segments — and instead writes its own
// separate `properties[Shade]` value.
//
// All segments share one generic init: click an option, mark it selected,
// echo its label next to the segment heading, recompute SKU/display. Shade
// additionally reacts to Stand changes: options whose hardware_color doesn't
// match the selected stand get disabled, with an automatic fallback to
// "No Shade" if the current pick becomes incompatible.
//
// Add to Order (initAddToOrder): separate from the native Add to Cart form
// above it. Does NOT call the backend directly — stores this configuration
// client-side (assets/configurator-order.js, since Shopify's real cart can't
// hold correctly-priced configured items) and sends the customer to the
// order-summary page, where they can add more products before checking out
// once. That page's Checkout action is what posts to the configurator-app's
// App Proxy endpoint to recompute prices and create the Draft Order. Exists
// because Shopify Cart Transform's price-override operation is Plus-only
// and this store isn't on Plus — see the "pricing mechanism decision"
// project memory.

import { addItem, MAX_QUANTITY, takeEditRestore } from '@theme/configurator-order';
import { formatMoney } from '@theme/money-formatting';
import { isTrade, fetchTradePrices, retailTradeHtml } from '@theme/trade-pricing';

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.sku-configurator').forEach(initConfigurator);
  // Belt-and-suspenders alongside the CSS sibling-selector hide in
  // socket-configurator.liquid (`.sku-configurator ~ .buy-buttons-block`) —
  // only runs at all when a configurator is actually on the page, so it
  // never touches Buy Buttons on other, not-yet-migrated product templates.
  if (document.querySelector('.sku-configurator')) {
    document.querySelectorAll('.buy-buttons-block').forEach((el) => {
      el.style.display = 'none';
    });
  }
});

const CHOICE_SELECTOR = '.sku-configurator__option, .sku-configurator__swatch, .sku-configurator__checkrow';

// The backend builds the real SKU by walking item.selections in array order
// (proxy.checkout.tsx), not a fixed schema — it's only ever been correct
// because state's key-insertion order happens to match DOM segment order.
// Optional Stand breaks that assumption (its state.stand entry lands after
// Shade/Socket/Cord, since it's set by initStandToggle() after the main
// segment loop runs), so initAddToOrder() sorts by this canonical order
// before submitting rather than trusting Object.entries(state) directly.
const SEGMENT_ORDER = ['finish', 'hardware_color', 'drop_side', 'chain', 'stand', 'shade', 'socket', 'cord', 'bulb'];

const MAX_CHAIN_SECTIONS = 5;

/** @typedef {{ id: string|null, code: string, label: string, hardwareColor: string|null, price: number }} ConfiguratorChoice */
/** @typedef {Record<string, ConfiguratorChoice>} ConfiguratorState */

/** @param {number} price */
function formatPrice(price) {
  return `+ $${price.toFixed(2)}`;
}

/** @param {HTMLElement} root */
function initConfigurator(root) {
  const skuInput = root.querySelector('input[name="properties[Configured SKU]"]');
  const displayInput = root.querySelector('input[name="properties[Configuration]"]');
  const shadeInput = root.querySelector('input[name="properties[Shade]"]');

  const fixed = {
    shapeId: root.dataset.shapeId,
    shapeName: root.dataset.shapeName,
  };

  // Edit flow: if the customer clicked Edit on the order-summary page for
  // this exact shape, restore() has their prior selections/quantity — read
  // once, up front, before any segment gets its default applied.
  const restore = takeEditRestore(fixed.shapeId);

  /** @type {ConfiguratorState} */
  const state = {};

  let quantity = 1;

  // Shapes with no Stand segment at all (e.g. Barnacle, Tangier — "no stand
  // by design" per the SKU matrix's Stand_Compatibility sheet) never get a
  // state.stand entry, since that segment simply isn't rendered for them —
  // the stand slot is just omitted from the SKU (client reversed the
  // earlier "NS" placeholder decision 2026-08-03).
  function buildSku() {
    const standCode = state.stand?.code || (root.hasAttribute('data-ns-in-sku') ? 'NS' : undefined);
    return [fixed.shapeId, state.finish.code, standCode, state.socket.code, state.cord.code]
      .filter(Boolean)
      .join('-');
  }

  function buildDisplay() {
    // Omit the stand phrase entirely for the "no-stand chosen" case (NS),
    // not just when state.stand is unset — otherwise this would read
    // "No Stand Stand | ", since state.stand.label is now literally "No
    // Stand" for optional-stand shapes with the toggle off (see clearStand()
    // in initStandToggle() — the SKU still needs the NS segment, the display
    // string just shouldn't say "Stand" twice).
    const standPart = (state.stand && state.stand.code !== 'NS') ? `${state.stand.label} Stand | ` : '';
    return `${fixed.shapeName} Table Lamp | ${state.finish.label} | ${standPart}${state.socket.label} | ${state.cord.label}`;
  }

  // The native <product-price> block only ever updates on a real Shopify
  // variant-select event (see product-price.js) — irrelevant here, since
  // every upcharge on this page comes from metaobject prices, not native
  // variant options, and this configurator's lamps (aside from the
  // Texture-as-variant shapes handled by initTextureVariantSync() below)
  // only ever have the one base variant anyway. So this writes the visible
  // price text directly instead, the same way order-summary.js's
  // unitPrice() computes a row total: base price + every selected
  // segment's (price * quantity), Chain/Bulb's real per-unit price
  // included since their `quantity` field already carries the multiplier
  // (see initChainQuantity()/initBulbQuantity()) — same two real inputs as
  // the backend, same number the customer will see again on the
  // order-summary page.
  // Trade customers only (2026-10-02, see assets/trade-pricing.js): the
  // catalog-to-retail ratio checkout applies to every add-on, plus the
  // variant's retail list price. Liquid only exposes the catalog price in a
  // B2B session (root.dataset.productPrice), so these come from the app.
  // Stays ratio 1 / null - i.e. the plain total below - for everyone else,
  // and whenever the lookup fails.
  const trade = { ratio: 1, retailBase: null };

  const fmtMoney = (dollars) =>
    formatMoney(Math.round(dollars * 100), root.dataset.moneyFormat || '${{amount}}', root.dataset.currency || 'USD');

  // One add-on's per-unit price at the catalog ratio, rounded to the cent
  // per unit exactly like checkout.server.ts (Math.round(price*100) after the
  // ratio is applied), so page, order summary and checkout agree to the cent.
  // Exactly the plain price whenever there's no trade ratio.
  function tradeUnit(price) {
    if (trade.ratio === 1) return price;
    return Math.round(price * trade.ratio * 100) / 100;
  }

  function currentVariantId() {
    const form = document.getElementById(root.dataset.productFormId);
    const input = form ? form.querySelector('input[name="id"]') : null;
    return input && input.value ? input.value : null;
  }

  // Add-on price labels ("+ $100.00" next to swatches, the selected-choice
  // text, the Speciality heading) are rendered at retail, by Liquid or by
  // formatPrice(). For trade customers rewrite each to its catalog-adjusted
  // amount, keeping the retail text in a tooltip. Idempotent: the original
  // retail amount is stored on first sight (data-retail) and every later
  // pass recomputes from it, so a ratio change (Texture variant switch)
  // or a re-created label is always right.
  function rewritePriceLabels() {
    if (!isTrade()) return;
    root.querySelectorAll('.sku-configurator__price').forEach((el) => {
      // Bulbs are never discounted (client, 2026-10-05) - their labels stay at list price.
      const isBulbLabel = el.closest('[data-segment="bulb"]') !== null;
      if (el.dataset.retail === undefined) {
        const amount = parseFloat((el.textContent || '').replace(/[^0-9.]/g, ''));
        if (!Number.isFinite(amount)) return;
        el.dataset.retail = String(amount);
      }
      const retail = Number(el.dataset.retail);
      // Chain/Bulb labels show a total of N units (data-qty): round per unit.
      const qty = Number(el.dataset.qty) || 1;
      if (trade.ratio === 1 || isBulbLabel) {
        el.textContent = `+ ${fmtMoney(retail)}`;
        el.removeAttribute('title');
      } else {
        const tradeTotal = Math.round(tradeUnit(retail / qty) * qty * 100) / 100;
        el.textContent = `+ ${fmtMoney(tradeTotal)}`;
        el.title = `Retail + ${fmtMoney(retail)}`;
      }
    });
  }

  // Labels stay hidden for trade customers until the ratio is known (see
  // .hb-trade-pending in assets/b2b-pricing.css), so they never flash retail.
  function releaseTradePending() {
    document.documentElement.classList.remove('hb-trade-pending');
  }

  function loadTradePricing() {
    if (!isTrade()) return;
    const variantId = currentVariantId();
    if (!variantId) {
      releaseTradePending();
      return;
    }
    fetchTradePrices([variantId]).then((prices) => {
      const p = prices[variantId];
      if (p && !p.none && p.retail !== null) {
        trade.ratio = p.ratio;
        trade.retailBase = p.retail;
      } else {
        trade.ratio = 1;
        trade.retailBase = null;
      }
      updateTotalPrice();
      releaseTradePending();
    });
  }

  function updateTotalPrice() {
    rewritePriceLabels();
    const priceEl = root.closest('.shopify-section')?.querySelector('product-price .price');
    if (!priceEl) return;
    const basePrice = Number(root.dataset.productPrice) || 0;
    const retailUpcharges = Object.values(state).reduce((sum, choice) => sum + (choice.price || 0) * (choice.quantity || 1), 0);
    if (trade.retailBase !== null && trade.ratio !== 1) {
      // Same rule as checkout: the catalog ratio applies to every add-on too.
      // Bulbs are never discounted: only the other add-ons take the catalog ratio.
      const tradeUpcharges = Object.entries(state).reduce(
        (sum, [segment, choice]) => sum + (segment === 'bulb' ? (choice.price || 0) : tradeUnit(choice.price || 0)) * (choice.quantity || 1),
        0,
      );
      priceEl.innerHTML = retailTradeHtml(fmtMoney(trade.retailBase + retailUpcharges), fmtMoney(basePrice + tradeUpcharges));
    } else {
      priceEl.textContent = fmtMoney(basePrice + retailUpcharges);
    }
  }

  function recompute() {
    if (state.finish && state.socket && state.cord) {
      if (skuInput) skuInput.value = buildSku();
      if (displayInput) displayInput.value = buildDisplay();
    }
    if (shadeInput && state.shade) {
      shadeInput.value = state.shade.label;
    }
    updateTotalPrice();
  }

  function applySelection(segmentEl, segmentName, optionEl) {
    segmentEl.querySelectorAll(CHOICE_SELECTOR).forEach((o) => o.classList.remove('is-selected'));
    optionEl.classList.add('is-selected');

    state[segmentName] = {
      id: optionEl.dataset.id || null,
      code: optionEl.dataset.code,
      label: optionEl.dataset.label,
      hardwareColor: optionEl.dataset.hardwareColor || null,
      // Only set on Shade/Bulb swatches — carried through so their split
      // line item at checkout can show a real photo too, same as the shape.
      swatchImage: optionEl.dataset.swatchImage || null,
      price: Number(optionEl.dataset.price) || 0,
    };

    const labelValueEl = segmentEl.querySelector('[data-label-value]');
    if (labelValueEl) {
      labelValueEl.textContent = optionEl.dataset.label;
      if (state[segmentName].price > 0) {
        const priceEl = document.createElement('span');
        priceEl.className = 'sku-configurator__price';
        priceEl.textContent = formatPrice(state[segmentName].price);
        labelValueEl.append(' ', priceEl);
      }
    }

    recompute();
  }

  root.querySelectorAll('[data-segment]').forEach((segmentEl) => {
    const segmentName = segmentEl.dataset.segment;

    // Toggle-gated segments (optional Stand, and Chain's No/Yes-then-reveal
    // UI) manage their own selection state entirely — state[segmentName]
    // must stay untouched by this generic loop, since its own init function
    // handles restoring a prior edit's choice itself. Chain specifically
    // reuses the .sku-configurator__checkrow class (part of CHOICE_SELECTOR)
    // purely for visual styling on its No/Yes rows, which have none of the
    // data-id/data-code/data-price attributes this loop expects — letting
    // it process them would both corrupt state.chain with undefined values
    // and double-fire alongside initChainQuantity()'s own click handlers.
    if (segmentEl.dataset.standOptional !== undefined) return;
    if (segmentName === 'chain') return;

    const options = segmentEl.querySelectorAll(CHOICE_SELECTOR);

    // Edit flow: prefer the restored code for this segment, if present and
    // still a real option (an option could've been renamed/removed in Admin
    // since this item was added — falls through to the normal default for
    // just that one segment, doesn't block the rest of the restore).
    let initial = null;
    const restoredSel = restore && restore.selections.find((sel) => sel.segment === segmentName);
    if (restoredSel) {
      initial = Array.from(options).find((o) => o.dataset.code === restoredSel.code) || null;
    }
    if (!initial) {
      initial = segmentEl.querySelector(`${CHOICE_SELECTOR}.is-selected`) || options[0];
    }

    if (initial) applySelection(segmentEl, segmentName, initial);

    options.forEach((optionEl) => {
      optionEl.addEventListener('click', () => {
        optionEl.querySelector('input[type="radio"]').checked = true;
        applySelection(segmentEl, segmentName, optionEl);
      });
    });
  });

  loadTradePricing();
  const refreshShadeAvailability = initShadeCompatibility();
  initStandToggle(refreshShadeAvailability);
  initChainQuantity();
  initBulbQuantity();
  initQuantity();
  initAddToOrder();
  initTextureVariantSync();

  function initQuantity() {
    const minusEl = root.querySelector('[data-qty-minus]');
    const plusEl = root.querySelector('[data-qty-plus]');
    const inputEl = root.querySelector('[data-qty-input]');
    if (!minusEl || !plusEl || !inputEl) return;

    inputEl.max = String(MAX_QUANTITY);

    function setQuantity(next) {
      quantity = Math.min(MAX_QUANTITY, Math.max(1, Math.round(next) || 1));
      inputEl.value = String(quantity);
    }

    if (restore && restore.quantity) setQuantity(restore.quantity);

    minusEl.addEventListener('click', () => setQuantity(quantity - 1));
    plusEl.addEventListener('click', () => setQuantity(quantity + 1));
    inputEl.addEventListener('change', () => setQuantity(Number(inputEl.value)));
  }

  function initAddToOrder() {
    const addToOrderEl = root.querySelector('[data-add-to-order]');
    const statusEl = root.querySelector('[data-add-to-order-status]');
    const successEl = root.querySelector('[data-add-to-order-success]');
    const successTextEl = root.querySelector('[data-add-to-order-success-text]');
    if (!addToOrderEl) return;

    const idleLabel = addToOrderEl.textContent;
    let revertTimer = null;

    addToOrderEl.addEventListener('click', () => {
      statusEl.textContent = '';
      if (successEl) successEl.hidden = true;

      const selections = Object.entries(state)
        .filter(([, choice]) => choice.id)
        .sort(([a], [b]) => SEGMENT_ORDER.indexOf(a) - SEGMENT_ORDER.indexOf(b))
        .map(([segment, choice]) => ({
          segment,
          metaobjectId: choice.id,
          code: choice.code,
          skuCode: choice.skuCode || null,
          label: choice.label,
          price: choice.price,
          // How many of this selection's real per-unit price to charge
          // (Chain sections, Bulb count) — proxy.checkout.tsx multiplies
          // its own server-fetched price by this rather than trusting
          // `price` above, which is display-only here. Segments without a
          // quantity concept just don't set choice.quantity, defaulting to
          // 1 (a no-op multiply), so this is safe to send unconditionally.
          quantity: choice.quantity || 1,
          image: choice.swatchImage || null,
          // Shade and Bulb are upsells, not part of the SKU/Configuration
          // string — Bulb matches Shade's treatment exactly per
          // Lookup_Bulbs ("Upsell SKUs, out of the product SKU"), including
          // becoming its own separate line item at checkout.
          excludeFromSku: segment === 'shade' || segment === 'bulb',
        }));

      if (selections.length === 0 || selections.length !== Object.keys(state).length) {
        statusEl.textContent = 'This product isn’t set up for checkout yet — missing option data.';
        return;
      }

      const form = document.getElementById(root.dataset.productFormId);
      const variantInput = form ? form.querySelector('input[name="id"]') : null;
      if (!variantInput || !variantInput.value) {
        statusEl.textContent = 'Could not find this product’s variant — try reloading the page.';
        return;
      }

      if (!root.dataset.orderPageUrl) {
        statusEl.textContent = 'This product isn’t set up for checkout yet — missing order page URL.';
        return;
      }

      addItem({
        variantId: `gid://shopify/ProductVariant/${variantInput.value}`,
        shapeId: fixed.shapeId,
        shapeName: fixed.shapeName,
        productUrl: root.dataset.productUrl || null,
        image: root.dataset.productImage || null,
        selections,
        unitBasePrice: Number(root.dataset.productPrice) || 0,
        nsInSku: root.hasAttribute('data-ns-in-sku'),
        quantity,
      });

      // No redirect — stays on the page so the customer can keep configuring
      // or browsing other products, and reaches their order whenever they
      // want via the success message's link (or the header cart icon).
      // The add itself is instant (client-side/localStorage), so the brief
      // "Adding…" state below is a deliberate perceived-feedback pause, not
      // an actual wait — then flips to "Added" alongside the message, then
      // reverts to normal after a moment.
      clearTimeout(revertTimer);
      addToOrderEl.disabled = true;
      addToOrderEl.classList.add('is-loading');
      addToOrderEl.textContent = 'Adding…';

      setTimeout(() => {
        addToOrderEl.classList.remove('is-loading');
        addToOrderEl.classList.add('is-added');
        addToOrderEl.textContent = 'Added';

        if (successEl) {
          if (successTextEl) successTextEl.textContent = `Added ${fixed.shapeName || 'item'} to your order.`;
          successEl.hidden = false;
        }

        revertTimer = setTimeout(() => {
          addToOrderEl.classList.remove('is-added');
          addToOrderEl.textContent = idleLabel;
          addToOrderEl.disabled = false;
        }, 1600);
      }, 350);
    });
  }

  // Returns the refresh function so initStandToggle() can also trigger it —
  // adding/removing a stand via the toggle changes state.stand exactly like
  // picking a different stand finish does, so Shade needs to re-check
  // compatibility either way.
  function initShadeCompatibility() {
    const standSegment = root.querySelector('[data-segment="stand"]');
    const shadeSegment = root.querySelector('[data-segment="shade"]');
    // Lamps with no stand section normally show every shade; flagged ones
    // (custom.brass_shades_only) behave like "no stand" = Brass hardware.
    const brassOnly = root.hasAttribute('data-brass-shades-only');
    if (!shadeSegment || (!standSegment && !brassOnly)) return null;

    const shadeChoices = shadeSegment.querySelectorAll(CHOICE_SELECTOR);

    function updateAvailability() {
      // No stand selected (optional-stand lamps like Ginger Mini) = the lamp
      // ships with the brass spider, so Brass shades are the ones that fit
      // (client, Trello "Shades on product pages are not right", 2026-10-02).
      const standHardware = state.stand ? state.stand.hardwareColor : 'Brass';
      let selectedIsInvalid = false;

      shadeChoices.forEach((choiceEl) => {
        const shadeHardware = choiceEl.dataset.hardwareColor;
        const compatible = !shadeHardware || shadeHardware === 'Universal' || shadeHardware === standHardware;
        // Hidden, not crossed out: fewer choices in front of the customer.
        choiceEl.hidden = !compatible;
        choiceEl.classList.remove('is-disabled');
        if (!compatible && choiceEl.classList.contains('is-selected')) selectedIsInvalid = true;
      });

      if (selectedIsInvalid) {
        const visibleChoices = Array.from(shadeChoices).filter((el) => !el.hidden);
        const fallback = visibleChoices.find((el) => el.dataset.code === '00') || visibleChoices[0];
        if (fallback) applySelection(shadeSegment, 'shade', fallback);
      }
    }

    if (standSegment) {
      standSegment.querySelectorAll(CHOICE_SELECTOR).forEach((standOptionEl) => {
        standOptionEl.addEventListener('click', updateAvailability);
      });
    }

    updateAvailability();
    return updateAvailability;
  }

  // Optional Stand ("ships without a stand by default, customer can add one"
  // — the matrix's Stand_Compatibility "Default NS + Alternative Yes" state).
  // Unlike Shade/Bulb, Stand's code feeds buildSku() when state.stand exists
  // at all, so this toggle controls that presence directly rather than just
  // switching between two real options.
  function initStandToggle(refreshShadeAvailability) {
    const segmentEl = root.querySelector('[data-stand-optional]');
    if (!segmentEl) return;

    const toggleEl = segmentEl.querySelector('[data-stand-toggle]');
    const swatchesEl = segmentEl.querySelector('[data-stand-swatches]');
    if (!toggleEl || !swatchesEl) return;

    const options = segmentEl.querySelectorAll(CHOICE_SELECTOR);

    function selectStand(optionEl) {
      swatchesEl.hidden = false;
      toggleEl.checked = true;
      applySelection(segmentEl, 'stand', optionEl);
      if (refreshShadeAvailability) refreshShadeAvailability();
    }

    function clearStand() {
      swatchesEl.hidden = true;
      toggleEl.checked = false;
      options.forEach((o) => o.classList.remove('is-selected'));
      // Client-confirmed 2026-08-24: the SKU must carry a literal "NS"
      // segment when no stand is added (matches the matrix README's "the
      // stand slot is never empty, NS fills it" rule, and drives MRPEasy's
      // cord-hole routing operation) — so toggle-off selects the real
      // `no-stand` metaobject entry (data-ns-* on segmentEl, see the Liquid
      // template) rather than leaving state.stand unset. Falls back to the
      // old omit-entirely behavior if the entry hasn't been created yet, so
      // this doesn't hard-fail on a shape missing the NS reference.
      if (segmentEl.dataset.nsId) {
        state.stand = {
          id: segmentEl.dataset.nsId,
          code: segmentEl.dataset.nsCode,
          label: segmentEl.dataset.nsLabel,
          hardwareColor: null,
          swatchImage: null,
          price: Number(segmentEl.dataset.nsPrice) || 0,
        };
      } else {
        delete state.stand;
      }
      recompute();
      if (refreshShadeAvailability) refreshShadeAvailability();
    }

    // Edit flow: a restored item that had a real stand selected should come
    // back checked, with that exact finish — same restoredSel pattern the
    // main segment loop uses for every other segment (skipped there for this
    // one specifically, see the `data-standOptional` guard above).
    const restoredSel = restore && restore.selections.find((sel) => sel.segment === 'stand');
    const restoredOption = restoredSel && Array.from(options).find((o) => o.dataset.code === restoredSel.code);
    if (restoredOption) {
      selectStand(restoredOption);
    } else {
      clearStand();
    }

    toggleEl.addEventListener('change', () => {
      if (toggleEl.checked) {
        selectStand(segmentEl.querySelector(`${CHOICE_SELECTOR}.is-selected`) || options[0]);
      } else {
        clearStand();
      }
    });

    options.forEach((optionEl) => {
      optionEl.addEventListener('click', () => {
        optionEl.querySelector('input[type="radio"]').checked = true;
        selectStand(optionEl);
      });
    });
  }

  // Extra Chain ("2 x 3ft Extra Chain (6ft)" — client-confirmed 2026-08-24:
  // 6ft/9ft are just multiples of the one real 3XC entry, not separate
  // metaobject codes). UI matches the Magento reference site: a required
  // No/Yes choice, quantity stepper revealed only once "Yes" is picked —
  // same toggle-then-reveal pattern as initStandToggle(). No swatch/
  // checkrow elements matching CHOICE_SELECTOR live inside this segment
  // that the main segment loop would touch (the No/Yes rows use their own
  // [data-chain-choice] markers instead), so this function is the only
  // thing that ever sets state.chain.
  function initChainQuantity() {
    const segmentEl = root.querySelector('[data-segment="chain"]');
    if (!segmentEl) return;

    const noEl = segmentEl.querySelector('[data-chain-choice="no"]');
    const yesEl = segmentEl.querySelector('[data-chain-choice="yes"]');
    const qtyWrap = segmentEl.querySelector('[data-chain-qty-wrap]');
    const minusEl = segmentEl.querySelector('[data-chain-qty-minus]');
    const plusEl = segmentEl.querySelector('[data-chain-qty-plus]');
    const inputEl = segmentEl.querySelector('[data-chain-qty-input]');
    // Contact-only chain (Bloom - Garland Plug-In): no picker, but the order
    // still records "No extra chain" so the SKU gets its chain slot (MRPeasy
    // requires 0XC, 2026-10-14).
    if (!noEl && segmentEl.dataset.chainNoId) {
      state.chain = {
        id: segmentEl.dataset.chainNoId,
        code: segmentEl.dataset.chainNoCode,
        skuCode: segmentEl.dataset.chainNoSku || null,
        label: segmentEl.dataset.chainNoLabel,
        hardwareColor: null,
        swatchImage: null,
        price: 0,
        quantity: 1,
      };
      return;
    }
    if (!noEl || !yesEl || !qtyWrap || !minusEl || !plusEl || !inputEl) return;

    const labelValueEl = segmentEl.querySelector('[data-label-value]');
    let sections = 0;

    function applyChain() {
      if (sections <= 0) {
        // "No extra chain" — still a real selection (its NO code is
        // excluded from the SKU by proxy.checkout.tsx's NONE_CODES), not an
        // absent one, matching Chain's Var2-slot role in the SKU format.
        if (segmentEl.dataset.chainNoId) {
          state.chain = {
            id: segmentEl.dataset.chainNoId,
            code: segmentEl.dataset.chainNoCode,
            // SKU segment if the entry has a sku_code (no chain = 0XC).
            skuCode: segmentEl.dataset.chainNoSku || null,
            label: segmentEl.dataset.chainNoLabel,
            hardwareColor: null,
            swatchImage: null,
            price: 0,
            quantity: 1,
          };
        } else {
          delete state.chain;
        }
      } else if (segmentEl.dataset.chainXcId) {
        const unitPrice = Number(segmentEl.dataset.chainXcPrice) || 0;
        const feet = sections * 3;
        state.chain = {
          id: segmentEl.dataset.chainXcId,
          code: segmentEl.dataset.chainXcCode,
          label: sections === 1 ? '3ft Extra Chain' : `${sections} x 3ft Extra Chain (${feet}ft)`,
          hardwareColor: null,
          swatchImage: null,
          // Real single-section price, NOT pre-multiplied — proxy.checkout.tsx
          // never trusted a client-multiplied price anyway (it always
          // re-fetches each metaobject's real price fresh and ignores
          // whatever `price` the client sent), so the actual checkout was
          // silently charging for 1 section regardless of how many were
          // picked, even though this page's own display showed the right
          // total. Real bug, found via a real order 2026-08-24. Fixed by
          // sending `quantity` as its own trusted field instead (see
          // initAddToOrder()) and letting the backend do the multiplication
          // itself against the price IT fetched — price here stays the
          // per-unit number so unitPrice() in configurator-order.js and the
          // backend compute the same total from the same two real inputs.
          price: unitPrice,
          quantity: sections,
        };
      }

      if (labelValueEl) {
        labelValueEl.textContent = state.chain ? state.chain.label : '';
        const displayTotal = state.chain ? state.chain.price * (state.chain.quantity || 1) : 0;
        if (displayTotal > 0) {
          const priceEl = document.createElement('span');
          priceEl.className = 'sku-configurator__price';
          priceEl.textContent = formatPrice(displayTotal);
          // Total of N units: rewritePriceLabels() needs the unit count to
          // round per unit like checkout does.
          priceEl.dataset.retail = String(displayTotal);
          priceEl.dataset.qty = String(state.chain.quantity || 1);
          labelValueEl.append(' ', priceEl);
        }
      }

      recompute();
    }

    function selectNo() {
      sections = 0;
      noEl.classList.add('is-selected');
      yesEl.classList.remove('is-selected');
      noEl.querySelector('input[type="radio"]').checked = true;
      qtyWrap.hidden = true;
      applyChain();
    }

    // "Yes" always means at least 1 section — going below that is what the
    // "No" row is for, so the stepper's minus button floors at 1 rather
    // than 0 while Yes is selected (matches min="1" on the input itself).
    function selectYes(initialSections) {
      sections = Math.max(1, Math.min(MAX_CHAIN_SECTIONS, Math.round(initialSections) || 1));
      yesEl.classList.add('is-selected');
      noEl.classList.remove('is-selected');
      yesEl.querySelector('input[type="radio"]').checked = true;
      qtyWrap.hidden = false;
      inputEl.value = String(sections);
      applyChain();
    }

    function setSections(next) {
      sections = Math.min(MAX_CHAIN_SECTIONS, Math.max(1, Math.round(next) || 1));
      inputEl.value = String(sections);
      applyChain();
    }

    noEl.addEventListener('click', selectNo);
    yesEl.addEventListener('click', () => selectYes(sections || 1));
    minusEl.addEventListener('click', () => setSections(sections - 1));
    plusEl.addEventListener('click', () => setSections(sections + 1));
    inputEl.addEventListener('change', () => setSections(Number(inputEl.value)));

    // Edit flow: restoredSel.quantity carries the real prior section count
    // directly now (initAddToOrder() sends it as its own field — see the
    // real pricing-bug fix 2026-08-24). Used to derive it from
    // restoredSel.price / unitPrice, back when price was pre-multiplied by
    // sections; that division would now always land on 1, since price is
    // the real per-unit number both here and at checkout.
    const restoredSel = restore && restore.selections.find((sel) => sel.segment === 'chain');
    const unitPrice = Number(segmentEl.dataset.chainXcPrice) || 0;
    if (restoredSel && restoredSel.code === segmentEl.dataset.chainXcCode && unitPrice > 0) {
      selectYes(restoredSel.quantity || 1);
    } else {
      selectNo();
    }
  }

  // Bulb quantity ("4 x Tala Sphere II Bulb" — client-confirmed 2026-08-24:
  // bulbs always price as multiples of the real single-unit entry, never a
  // flat-priced bundle SKU like the old "4 x Tala II Bulb" $116 one). Unlike
  // Chain/Stand, Bulb IS still processed by the generic segment loop above
  // (it's a plain always-visible swatch grid, no toggle) — this function
  // runs afterward and adjusts whatever that loop already selected, rather
  // than owning the segment itself. Recomputes fully from each swatch's own
  // raw data-price on every call (never from state.bulb.price, which this
  // function itself may have already touched) so repeated clicks or the
  // edit-restore flow can't compound. Stores the real per-unit price plus a
  // separate `quantity` field — see applyQuantity() below for why this
  // isn't pre-multiplied into `price` directly.
  function initBulbQuantity() {
    const segmentEl = root.querySelector('[data-segment="bulb"]');
    if (!segmentEl) return;

    // The cap: the product's bulb_quantity metafield (how many bulbs the
    // fixture takes). The customer picks 1..cap with the stepper, starting at
    // 1 (even when the cap is 12); "No Bulb" is the 0 case. The stepper stays
    // visible for as long as a real bulb is selected. (2026-10-08, client to
    // confirm cap vs. no cap.)
    const cap = Number(segmentEl.dataset.bulbQuantity) || 1;
    if (cap <= 1) return; // single-bulb shapes: nothing to multiply or choose

    const labelValueEl = segmentEl.querySelector('[data-label-value]');
    const wrapEl = segmentEl.querySelector('[data-bulb-qty-wrap]');
    const inputEl = segmentEl.querySelector('[data-bulb-qty-input]');
    const minusEl = segmentEl.querySelector('[data-bulb-qty-minus]');
    const plusEl = segmentEl.querySelector('[data-bulb-qty-plus]');

    const clamp = (n) => Math.min(cap, Math.max(1, Math.round(Number(n)) || 1));
    // Edit flow: come back with the same number of bulbs.
    const restoredBulb = restore && restore.selections.find((sel) => sel.segment === 'bulb');
    let chosen = restoredBulb && restoredBulb.quantity ? clamp(restoredBulb.quantity) : 1;

    function applyQuantity(optionEl) {
      const isNoBulb = !optionEl || optionEl.dataset.code === '00';
      if (wrapEl) wrapEl.hidden = isNoBulb;
      if (isNoBulb) return; // No Bulb - nothing to multiply
      const unitPrice = Number(optionEl.dataset.price) || 0;
      if (inputEl) inputEl.value = String(chosen);

      // Real per-unit price, NOT pre-multiplied - proxy.checkout.tsx never
      // trusts a client-multiplied price, it re-fetches each metaobject's
      // real price and multiplies by the trusted `quantity` sent below.
      // This local `price` stays per-unit so unitPrice() in
      // configurator-order.js computes the same total the backend will.
      state.bulb = {
        ...state.bulb,
        price: unitPrice,
        quantity: chosen,
        label: chosen > 1 ? `${chosen} x ${optionEl.dataset.label}` : optionEl.dataset.label,
      };

      if (labelValueEl) {
        labelValueEl.textContent = state.bulb.label;
        const oldPrice = labelValueEl.querySelector('.sku-configurator__price');
        if (oldPrice) oldPrice.remove();
        const displayTotal = unitPrice * chosen;
        if (displayTotal > 0) {
          const priceEl = document.createElement('span');
          priceEl.className = 'sku-configurator__price';
          priceEl.textContent = formatPrice(displayTotal);
          priceEl.dataset.retail = String(displayTotal);
          priceEl.dataset.qty = String(chosen);
          labelValueEl.append(' ', priceEl);
        }
      }

      recompute();
    }

    // (Array.find, not a selector string: CHOICE_SELECTOR is a comma list, so
    // appending ".is-selected" would only apply to its LAST alternative and
    // match the first swatch - "No Bulb" - instead of the selected one.)
    const selectedOption = () =>
      Array.from(segmentEl.querySelectorAll(CHOICE_SELECTOR)).find((el) => el.classList.contains('is-selected'));
    const setChosen = (n) => {
      chosen = clamp(n);
      applyQuantity(selectedOption());
    };

    segmentEl.querySelectorAll(CHOICE_SELECTOR).forEach((optionEl) => {
      optionEl.addEventListener('click', () => applyQuantity(optionEl));
    });
    if (minusEl) minusEl.addEventListener('click', () => setChosen(chosen - 1));
    if (plusEl) plusEl.addEventListener('click', () => setChosen(chosen + 1));
    if (inputEl) inputEl.addEventListener('change', () => setChosen(inputEl.value));

    // Apply to whatever the generic segment loop already selected on load
    // (the default swatch, or a restored edit's choice - this runs after
    // that loop, so it always sees the final initial selection).
    applyQuantity(selectedOption());
  }

  // Texture-as-variant (Faux Bois/Tangier merge, 2026-09-07). Only fires
  // anything at all on the handful of shapes migrated onto one product with
  // a real "Texture" variant option (data-variant-sku-map present) — every
  // other shape's root has no such attribute, so this returns immediately
  // and adds zero overhead/listeners for the ~140 untouched single-variant
  // products. No new metafields: the real per-texture base SKU is read
  // straight from the native <variant-picker>'s own selected-variant event,
  // matched against variantSkuMap (built server-side in
  // socket-configurator.liquid from each variant's own `sku` field, hyphens
  // stripped — see that file's comment for why that's verified-safe).
  function initTextureVariantSync() {
    let variantSkuMap;
    try {
      variantSkuMap = JSON.parse(root.dataset.variantSkuMap || '');
    } catch {
      return;
    }
    if (!variantSkuMap || typeof variantSkuMap !== 'object') return;

    // Same event <variant-picker> fires on every real variant change
    // (assets/variant-picker.js) — assets/product-price.js is the existing
    // reference listener for this exact pattern in this theme: listen on
    // the section, await event.promise (resolves once the section-render
    // fetch completes), read the new variant off the resolved payload.
    const sectionEl = root.closest('.shopify-section, dialog');
    if (!sectionEl) return;

    const specialityGroup = root.querySelector('[data-finish-group="speciality"]');

    sectionEl.addEventListener('shopify:product:select', (event) => {
      // Same filter product-price.js applies — this configurator only ever
      // lives on a real product page, not a collection-grid product-card's
      // own swatch picker, but the event can still bubble from one if a
      // related-products block happens to render nearby.
      if (event.target instanceof Element && event.target.closest('product-card')) return;
      if (!event.promise) return;

      event.promise
        .then(({ variant }) => {
          if (!variant) return;
          const newSku = variantSkuMap[variant.id];
          if (newSku) {
            fixed.shapeId = newSku;
            root.dataset.productPrice = String(variant.price?.amount ?? root.dataset.productPrice);
          }
          const isDefaultTexture = Array.isArray(variant.selectedOptions)
            && variant.selectedOptions.some((opt) => opt.name === 'Texture' && opt.value === 'Default');
          if (specialityGroup) specialityGroup.hidden = !isDefaultTexture;
          recompute();
          loadTradePricing();
        })
        .catch(() => {
          // A cancelled/superseded section-render fetch (rapid re-clicks) —
          // nothing to recover, the next successful select event wins.
        });
    });
  }
}
